import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { GraphQLClient } from "graphql-request";
import { print } from "graphql";

// Same origin, so the HttpOnly session cookie is sent and the backend's origin
// check passes. In development Vite proxies /graphql to :8090 for the same reason —
// pointing the browser at a different origin would exercise a code path production
// never uses.
const client = new GraphQLClient("/graphql", { credentials: "same-origin" });

export class UnauthorizedError extends Error {
  constructor() {
    super("unauthorized");
    this.name = "UnauthorizedError";
  }
}

/**
 * ForbiddenError is a refusal: the caller is signed in and is not allowed this.
 *
 * A class rather than a message, because the alternative is every screen matching
 * `"forbidden: …"` as text — a coupling to the backend's prose across two languages
 * with no compiler and no test between them. The backend labels these with
 * extensions.code (internal/httpapi/graphqlerror.go); this is the other half.
 *
 * Keeps the message for a mutation's error toast, which is still the only report a
 * refused write gets. Screens do not render it: see ReadFailure.
 */
export class ForbiddenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ForbiddenError";
  }
}

/**
 * request runs one typed operation.
 *
 * A 401 becomes UnauthorizedError so the app can send the caller to the login
 * screen once, centrally, rather than each screen guessing what an error means.
 * A refusal becomes ForbiddenError for the same reason, read off the error code
 * the backend attaches rather than off its wording.
 */
export async function request<TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  variables?: TVariables
): Promise<TResult> {
  try {
    return await client.request<TResult>(print(document), variables ?? {});
  } catch (err) {
    // A resolver answering UNAUTHENTICATED means the middleware that should have
    // answered 401 did not run, so it wants the same login redirect rather than an
    // access-denied state the caller cannot act on.
    if (isUnauthorized(err) || hasCode(err, "UNAUTHENTICATED")) {
      throw new UnauthorizedError();
    }
    if (hasCode(err, "FORBIDDEN")) {
      throw new ForbiddenError(readableMessage(err));
    }
    throw new Error(readableMessage(err));
  }
}

function isUnauthorized(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 401;
}

/** hasCode reports whether any GraphQL error in the response carries `code`. */
function hasCode(err: unknown, code: string): boolean {
  const errors = (err as { response?: { errors?: Array<{ extensions?: { code?: unknown } }> } })
    ?.response?.errors;
  return errors?.some((e) => e?.extensions?.code === code) ?? false;
}

/**
 * shouldRetry decides whether a failed read is worth sending again.
 *
 * Neither refusal is: a 401 only delays the login redirect, and a forbidden read
 * will never succeed either. That second half used to be a claim the predicate did
 * not implement, because a refusal arrived as a plain Error and there was nothing
 * here to test for. ForbiddenError is what makes it true.
 *
 * Exported so it is testable — inline in main.tsx, next to createRoot, it was not.
 */
export function shouldRetry(failureCount: number, error: Error): boolean {
  if (error instanceof UnauthorizedError || error instanceof ForbiddenError) {
    return false;
  }
  return failureCount < 2;
}

/**
 * readableMessage pulls the first GraphQL error message out, because the default
 * stringification of a graphql-request error is the whole request body and is
 * unreadable in a toast.
 */
function readableMessage(err: unknown): string {
  const errors = (err as { response?: { errors?: Array<{ message?: string }> } })?.response?.errors;
  const first = errors?.[0]?.message;
  if (first) {
    return first;
  }
  return err instanceof Error ? err.message : "something went wrong";
}
